import React, { useState, useMemo } from 'react';
import { InvitationType } from '../../_graphql/generated/operations';
import { InvitationDesign } from '../../_types/invitation-design-v2';
import { INVITATION_TEMPLATES_V2 } from '../../_data/invitation-templates-v2';
import InvitationPreviewV2 from '../InvitationPreview/InvitationPreviewV2';

interface OnboardingWizardProps {
  invitationTypes: InvitationType[];
  onComplete: (invitationType: InvitationType, template: InvitationDesign) => void;
}

// Define the filter hierarchy
const FILTER_HIERARCHY = {
  wedding: {
    label: 'Wedding',
    icon: '💍',
    subcategories: {
      classic: { label: 'Classic', styles: ['elegant', 'traditional', 'formal'] },
      modern: { label: 'Modern', styles: ['minimal', 'contemporary', 'geometric'] },
      rustic: { label: 'Rustic', styles: ['vintage', 'country', 'natural'] },
      romantic: { label: 'Romantic', styles: ['garden', 'whimsical', 'watercolor', 'lace'] },
      beach: { label: 'Beach', styles: ['sunset', 'tropical', 'coastal'] }
    }
  },
  engagement: {
    label: 'Engagement',
    icon: '💑',
    subcategories: {
      party: { label: 'Party', styles: ['celebration', 'cocktail', 'garden', 'bbq'] },
      announcement: { label: 'Announcement', styles: ['elegant', 'modern', 'photo'] }
    }
  },
  savedate: {
    label: 'Save the Date',
    icon: '📅',
    subcategories: {
      simple: { label: 'Simple', styles: ['minimal', 'calendar', 'date'] },
      photo: { label: 'Photo', styles: ['placeholder', 'memory', 'couple'] },
      destination: { label: 'Destination', styles: ['map', 'travel', 'location'] }
    }
  },
  birthday: {
    label: 'Birthday',
    icon: '🎂',
    subcategories: {
      kids: { label: 'Kids', styles: ['rainbow', 'fun', 'colorful', 'child'] },
      milestone: { label: 'Milestone', styles: ['sweet-sixteen', 'thirty', 'fifty', 'elegant'] },
      first: { label: 'First Birthday', styles: ['one-derful', 'baby', 'cute'] }
    }
  },
  anniversary: {
    label: 'Anniversary',
    icon: '💐',
    subcategories: {
      silver: { label: '25th Silver', styles: ['silver', 'elegant', 'traditional'] },
      golden: { label: '50th Golden', styles: ['golden', 'elegant', 'traditional'] },
      ruby: { label: '40th Ruby', styles: ['ruby', 'red', 'romantic'] }
    }
  }
};

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ invitationTypes, onComplete }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<InvitationDesign | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 3-stage filter state
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);

  // Get available styles based on selected subcategory
  const availableStyles = useMemo(() => {
    if (!selectedEventType || !selectedSubcategory) return [];
    const eventType = FILTER_HIERARCHY[selectedEventType as keyof typeof FILTER_HIERARCHY];
    if (!eventType) return [];
    const subcategory = eventType.subcategories[selectedSubcategory as keyof typeof eventType.subcategories];
    return subcategory?.styles || [];
  }, [selectedEventType, selectedSubcategory]);

  // Filter templates based on search and 3-stage filters
  const filteredTemplates = useMemo(() => {
    return INVITATION_TEMPLATES_V2.filter(template => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = template.template.name.toLowerCase().includes(query);
        const matchesCategory = template.template.category.toLowerCase().includes(query);
        const matchesTags = template.metadata?.tags?.some(tag => 
          tag.toLowerCase().includes(query)
        );
        if (!matchesName && !matchesCategory && !matchesTags) {
          return false;
        }
      }

      // Event type filter
      if (selectedEventType) {
        const category = template.template.category.toLowerCase();
        
        // Handle special cases for category matching
        if (selectedEventType === 'savedate' && category !== 'save-the-date') return false;
        if (selectedEventType !== 'savedate' && category !== selectedEventType) return false;
      }

      // Style filter (if a style is selected)
      if (selectedStyle) {
        const templateTags = template.metadata?.tags || [];
        if (!templateTags.some(tag => tag.toLowerCase() === selectedStyle.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [searchQuery, selectedEventType, selectedStyle]);

  const handleSelectTemplate = (template: InvitationDesign) => {
    const defaultInvitationType = invitationTypes[0];
    if (defaultInvitationType) {
      onComplete(defaultInvitationType, template);
    }
  };

  const resetFilters = () => {
    setSelectedEventType(null);
    setSelectedSubcategory(null);
    setSelectedStyle(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Choose a Template</h2>
          
          {/* Search and Filter */}
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              />
              <svg 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* 3-Stage Filters */}
            <div className="space-y-4">
              {/* Stage 1: Event Type */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">1. Select event type:</p>
                  {selectedEventType && (
                    <button
                      onClick={resetFilters}
                      className="text-sm text-rose-600 hover:text-rose-700"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(FILTER_HIERARCHY).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedEventType(key);
                        setSelectedSubcategory(null);
                        setSelectedStyle(null);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        selectedEventType === key
                          ? 'bg-rose-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{value.icon}</span>
                      {value.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stage 2: Subcategory */}
              {selectedEventType && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">2. Select specific type:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(FILTER_HIERARCHY[selectedEventType as keyof typeof FILTER_HIERARCHY].subcategories).map(([key, value]) => (
                      <button
                        key={key}
                        onClick={() => {
                          setSelectedSubcategory(key);
                          setSelectedStyle(null);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedSubcategory === key
                            ? 'bg-rose-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {value.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stage 3: Style */}
              {selectedSubcategory && availableStyles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">3. Select style (optional):</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedStyle(null)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedStyle === null
                          ? 'bg-rose-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      All Styles
                    </button>
                    {availableStyles.map(style => (
                      <button
                        key={style}
                        onClick={() => setSelectedStyle(style)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
                          selectedStyle === style
                            ? 'bg-rose-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {style.replace('-', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 21a9 9 0 110-18 9 9 0 010 18z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-6">
              Found {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}. You can customize everything later!
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredTemplates.map((template) => (
                <button
                  key={template.template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-all overflow-hidden border-2 border-transparent hover:border-rose-200 group"
                >
                  {/* Template Preview */}
                  <div className="aspect-[5/7] relative overflow-hidden bg-gray-50">
                    <div className="absolute inset-0 scale-[0.5] origin-top-left" style={{ width: '200%', height: '200%' }}>
                      <InvitationPreviewV2
                        design={template}
                        invitation={{
                          id: 'preview',
                          event: {
                            id: 'preview',
                            eventName: 'Sarah & Michael\'s Wedding',
                            eventDateTime: new Date('2025-06-15').getTime()
                          }
                        }}
                        isPreview={true}
                      />
                    </div>
                  </div>
                  
                  {/* Template Info */}
                  <div className="p-3">
                    <h4 className="font-semibold text-sm text-gray-900 mb-1 group-hover:text-rose-600 transition-colors truncate">
                      {template.template.name}
                    </h4>
                    <p className="text-xs text-gray-500 capitalize">
                      {template.template.category}
                    </p>
                    {template.metadata?.tags && template.metadata.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.metadata.tags.slice(0, 2).map(tag => (
                          <span 
                            key={tag}
                            className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full capitalize"
                          >
                            {tag}
                          </span>
                        ))}
                        {template.metadata.tags.length > 2 && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{template.metadata.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;